import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 11050,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  header: {
    padding: '20px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '500',
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    padding: '0 20px',
    borderBottom: '1px solid #e0e0e0',
  },
  tab: {
    padding: '12px 16px',
    border: 'none',
    borderBottom: '3px solid transparent',
    background: 'none',
    color: '#666',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  activeTab: {
    borderBottomColor: '#0A3696',
    color: '#0A3696',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '4px',
    lineHeight: '1',
    color: '#666',
  },
  content: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1,
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '12px',
  },
  divider: {
    height: '1px',
    backgroundColor: '#e0e0e0',
    margin: '20px 0',
  },
  uploadArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  uploadButton: {
    padding: '8px 16px',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  logoPreview: {
    height: '50px',
    maxWidth: '200px',
    objectFit: 'contain',
  },
  radioGroup: {
    display: 'flex',
    gap: '20px',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  textField: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    marginTop: '8px',
  },
  helperText: {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  switchLabel: {
    fontSize: '14px',
    fontWeight: '400',
  },
  toggleButton: {
    position: 'relative',
    width: '50px',
    height: '24px',
    backgroundColor: '#ccc',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  toggleButtonActive: {
    backgroundColor: '#1976d2',
  },
  toggleSlider: {
    position: 'absolute',
    top: '2px',
    left: '2px',
    width: '20px',
    height: '20px',
    backgroundColor: 'white',
    borderRadius: '50%',
    transition: 'transform 0.3s',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  toggleSliderActive: {
    transform: 'translateX(26px)',
  },
  footer: {
    padding: '16px 20px',
    // borderTop: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  button: {
    padding: '8px 20px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  primaryButton: {
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
  },
};

const ProjectSettingsModal = ({ open, onClose, projectData, dashboardSettings, onSave, updateDashboardProjectName, t, user, notify, units, ...props}) => {
  const [loadingDashboardProjectName, setLoadingDashboardProjectName] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const isImperial = units === 'ft';
  const [settings, setSettings] = useState({
    waterSavedSettings: projectData?.waterSavedSettings || {
      enabled: false,
      threshold: ''
    },
    // Dashboard display settings
    is_maintenance_view: dashboardSettings?.is_maintenance_view ?? 1,
    is_watersave_view: dashboardSettings?.is_watersave_view ?? 1,
    is_minimal_view: dashboardSettings?.is_minimal_view ?? 0,
    // Water saved display unit: false = kgal (default), true = USG
    unit_is_usg: dashboardSettings?.unit_is_usg ?? false,
    // Custom project name for dashboard
    dashboard_display_name: projectData.dashboard_name || '',
    // Potential pipe coverage
    potential_pipe_coverage: dashboardSettings?.potential_pipe_coverage || ''
  });

  // Update settings when dashboardSettings change
  useEffect(() => {
    if (dashboardSettings) {
      setSettings(prev => ({
        ...prev,
        is_maintenance_view: dashboardSettings.is_maintenance_view ?? 1,
        is_watersave_view: dashboardSettings.is_watersave_view ?? 1,
        is_minimal_view: dashboardSettings.is_minimal_view ?? 0,
        unit_is_usg: dashboardSettings.unit_is_usg ?? false,
      }));
    }
  }, [dashboardSettings, projectData]);

  useEffect(() => {
    if (open) {
      setActiveTab('general');
    }
  }, [open]);

  const handleWaterSavedChange = (field, value) => {
    setSettings({
      ...settings,
      waterSavedSettings: {
        ...settings.waterSavedSettings,
        [field]: value
      }
    });
  };

  const handleDisplaySettingChange = (field, checked) => {
    setSettings({
      ...settings,
      [field]: checked ? 1 : 0
    });
  };

  const handleSave = () => {
    onSave(settings);
    onClose();
  };

  if (!open) return null;

  return createPortal(
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>{t('project_settings')}</h2>
          <button style={modalStyles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div style={modalStyles.tabs} role="tablist" aria-label={t('project_settings')}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'general'}
            style={{
              ...modalStyles.tab,
              ...(activeTab === 'general' ? modalStyles.activeTab : {}),
            }}
            onClick={() => setActiveTab('general')}
          >
            {t('general')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'views'}
            style={{
              ...modalStyles.tab,
              ...(activeTab === 'views' ? modalStyles.activeTab : {}),
            }}
            onClick={() => setActiveTab('views')}
          >
            Views
          </button>
        </div>

        <div style={modalStyles.content}>
          {/* Custom Dashboard Name - Only visible to AQS Admin */}
          {activeTab === 'general' && user?.isAQS && (
            <div style={modalStyles.section}>
              <h3 style={modalStyles.sectionTitle}>{t('dashboard_display_name')}</h3>
              <div style={{ display: 'flex', gap: '3px', alignItems: 'baseline' }}>
                <input
                  type="text"
                  value={settings.dashboard_display_name}
                  onChange={(e) => setSettings({ ...settings, dashboard_display_name: e.target.value })}
                  placeholder={projectData?.ShortName || t('enter_custom_name')}
                  style={modalStyles.textField}
                />
                <button
                  disabled={loadingDashboardProjectName}
                  style={{
                    ...modalStyles.button,
                    ...modalStyles.primaryButton,
                    backgroundColor: loadingDashboardProjectName ? '#a5a5a5' : '#1976d2'
                  }}
                  onClick={async () => {
                    setLoadingDashboardProjectName(true);
                    const res = await updateDashboardProjectName(projectData.ID, settings.dashboard_display_name);
                    setLoadingDashboardProjectName(false);
                    
                    if (!res.status) {
                      notify(res.err?.message || 'Failed', 'error')
                    } else {
                      notify('update success', 'success')
                    }
                  }}
                >
                  {t('save')}
                </button>
              </div>
            </div>
          )}

          {/* Dashboard Display Settings */}
          {activeTab === 'views' && (
            <div style={modalStyles.section}>
              <h3 style={modalStyles.sectionTitle}>{t('dashboard_display_settings')}</h3>

              {/* Display Maintenance View Toggle */}
              <div style={modalStyles.switchContainer}>
                <span style={modalStyles.switchLabel}>{t('display_maintenance_view')}</span>
                <div
                  style={{
                    ...modalStyles.toggleButton,
                    ...(settings.is_maintenance_view === 1 ? modalStyles.toggleButtonActive : {})
                  }}
                  onClick={() => handleDisplaySettingChange('is_maintenance_view', settings.is_maintenance_view !== 1)}
                >
                  <div
                    style={{
                      ...modalStyles.toggleSlider,
                      ...(settings.is_maintenance_view === 1 ? modalStyles.toggleSliderActive : {})
                    }}
                  />
                </div>
              </div>

              {/* Display Water Saved View Toggle */}
              <div style={modalStyles.switchContainer}>
                <span style={modalStyles.switchLabel}>{t('display_water_saved_view')}</span>
                <div
                  style={{
                    ...modalStyles.toggleButton,
                    ...(settings.is_watersave_view === 1 ? modalStyles.toggleButtonActive : {})
                  }}
                  onClick={() => handleDisplaySettingChange('is_watersave_view', settings.is_watersave_view !== 1)}
                >
                  <div
                    style={{
                      ...modalStyles.toggleSlider,
                      ...(settings.is_watersave_view === 1 ? modalStyles.toggleSliderActive : {})
                    }}
                  />
                </div>
              </div>

              {/* Display Minimal View Toggle */}
              <div style={modalStyles.switchContainer}>
                <span style={modalStyles.switchLabel}>{t('display_minimal_view')}</span>
                <div
                  style={{
                    ...modalStyles.toggleButton,
                    ...(settings.is_minimal_view === 1 ? modalStyles.toggleButtonActive : {})
                  }}
                  onClick={() => handleDisplaySettingChange('is_minimal_view', settings.is_minimal_view !== 1)}
                >
                  <div
                    style={{
                      ...modalStyles.toggleSlider,
                      ...(settings.is_minimal_view === 1 ? modalStyles.toggleSliderActive : {})
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'general' && <div style={modalStyles.divider} />}

          {/* Water Saved Settings */}
          {activeTab === 'general' && (
            <div style={modalStyles.section}>
              <h3 style={modalStyles.sectionTitle}>{t('water_saved_settings')}</h3>

              {/* Enable Water Saved Tracking Toggle */}
              <div style={modalStyles.switchContainer}>
                <span style={modalStyles.switchLabel}>{t('enable_water_saved_tracking')}</span>
                <div
                  style={{
                    ...modalStyles.toggleButton,
                    ...(settings.waterSavedSettings.enabled ? modalStyles.toggleButtonActive : {})
                  }}
                  onClick={() => handleWaterSavedChange('enabled', !settings.waterSavedSettings.enabled)}
                >
                  <div
                    style={{
                      ...modalStyles.toggleSlider,
                      ...(settings.waterSavedSettings.enabled ? modalStyles.toggleSliderActive : {})
                    }}
                  />
                </div>
              </div>

              {settings.waterSavedSettings.enabled && (
                <div>
                  <label>{t('threshold_value')}</label>
                  <input
                    type="number"
                    value={settings.waterSavedSettings.threshold}
                    onChange={(e) => handleWaterSavedChange('threshold', e.target.value)}
                    style={modalStyles.textField}
                  />
                </div>
              )}

              {/* Water Saved Display Unit (imperial only) */}
              {isImperial && (
                <div style={{ marginTop: '16px' }}>
                  <span style={modalStyles.switchLabel}>{t('water_saved_display_unit')}</span>
                  <div style={{ ...modalStyles.radioGroup, marginTop: '8px' }}>
                    <label style={modalStyles.radioLabel}>
                      <input
                        type="radio"
                        name="water_saved_unit"
                        value="kgal"
                        checked={settings.unit_is_usg === false}
                        onChange={() => setSettings({ ...settings, unit_is_usg: false })}
                      />
                      {t('kgal')}
                    </label>
                    <label style={modalStyles.radioLabel}>
                      <input
                        type="radio"
                        name="water_saved_unit"
                        value="usg"
                        checked={settings.unit_is_usg === true}
                        onChange={() => setSettings({ ...settings, unit_is_usg: true })}
                      />
                      {t('usg')}
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={modalStyles.footer}>
          <button style={modalStyles.button} onClick={onClose}>
            {t('cancel')}
          </button>
          <button style={{ ...modalStyles.button, ...modalStyles.primaryButton }} onClick={handleSave}>
            {t('save')} {t('project_settings')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

ProjectSettingsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectData: PropTypes.object,
  dashboardSettings: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  updateDashboardProjectName: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  user: PropTypes.object,
  notify: PropTypes.func.isRequired,
  units: PropTypes.string
};

export default ProjectSettingsModal;
